import type { AxePurchaseState, v2 } from '@deque/billing-service-client';
import {
  getDistinctIdCookie,
  setDistinctIdCookie,
  type Cookie
} from '@deque/distinct-id-client';
import type { Config } from 'amplitude-js';
import amplitudeJs from 'amplitude-js';
import TagManager from 'react-gtm-module';
import removeTrailingSlash from 'remove-trailing-slash';

import type { SignedUpUser } from './api-client';
import {
  ACTIVE_STATES,
  USER_JOB_ROLES,
  type ProductSlugs,
  type SupportedIntegrationProductSlugs
} from './constants';
import { type AuthUser } from './contexts/auth';

const ENTERPRISE_USER_PROPERTY_KEYS = [
  'enterpriseId',
  'enterpriseName',
  'isOrgAdmin',
  'subscriptions'
] as const;

// Returns the set of canonical `product_slug` values for every subscription on
// the enterprise that is currently active (paid or trialing). Used as an
// array-typed user property so charts/cohorts can filter via
// `subscriptions contains X` rather than maintaining a parallel boolean per
// product. Deduped because a single enterprise can hold multiple active
// subscriptions for the same product (e.g. across environments) and we want
// `subscriptions` to represent the *set* of products, not the count.
function getActiveSubscriptions(
  subscriptions: v2.EnterpriseSubscription[] = []
): string[] {
  return Array.from(
    new Set(
      subscriptions
        .filter(sub =>
          ACTIVE_STATES.includes(sub.purchase_state as AxePurchaseState)
        )
        .map(sub => sub.product_slug)
    )
  );
}

const DEFAULT_TIMEOUT = 5000; // Default timeout for amplitude calls in ms

export enum AnalyticsAmplitudeEvents {
  SIGNUP = 'conversion:signup',
  VIEW_SIGNUP = 'conversion:view:signup',
  CLICK_GITHUB = 'conversion:click:github',
  CLICK_GOOGLE = 'conversion:click:google',
  CLICK_CREATE_ACCOUNT = 'conversion:click:createAccount',
  CLICK_AXE_DEVTOOLS = 'conversion:click:axeDevtools',
  TRIAL_START = 'conversion:trialstart',
  ACCEPT_TERMS = 'conversion:terms:accept',
  CLICK_ADD_USERS = 'expansion:addUsers:click',
  CLICK_CHANGE_PLAN = 'expansion:changePlan:click',
  SUBMIT_LICENSE_CHANGE = 'expansion:submit',
  INTEGRATION_CONNECT = 'integration:connect',
  INTEGRATION_CONNECT_FAIL = 'integration:connect:fail',
  INTEGRATION_DISCONNECT = 'integration:disconnect',
  INTEGRATION_DISCONNECT_FAIL = 'integration:disconnect:fail',
  INTEGRATION_TEMPLATE_ADD_CLICK = 'integration:template:add:click',
  INTEGRATION_TEMPLATE_ADD_CANCEL = 'integration:template:add:cancel',
  INTEGRATION_TEMPLATE_ADD_SAVE = 'integration:template:add:save',
  INTEGRATION_TEMPLATE_ADD_FAIL = 'integration:template:add:fail',
  INTEGRATION_TEMPLATE_EDIT_CLICK = 'integration:template:edit:click',
  INTEGRATION_TEMPLATE_EDIT_CANCEL = 'integration:template:edit:cancel',
  INTEGRATION_TEMPLATE_EDIT_SAVE = 'integration:template:edit:save',
  INTEGRATION_TEMPLATE_EDIT_FAIL = 'integration:template:edit:fail',
  INTEGRATION_TEMPLATE_DELETE_CLICK = 'integration:template:delete:click',
  INTEGRATION_TEMPLATE_DELETE_FAIL = 'integration:template:delete:fail',
  INTEGRATION_TEMPLATE_DELETE_SUCCESS = 'integration:template:delete:success',
  INTEGRATION_ISSUE_SEND_CLICK = 'integration:issue:send:click',
  INTEGRATION_ISSUE_SEND_SUCCESS = 'integration:issue:send:success',
  INTEGRATION_ISSUE_SEND_FAILURE = 'integration:issue:send:failure',
  USER_MANAGEMENT_EXPORT_SUCCESS = 'user-management:export:success',
  INTEGRATION_TEST_SEND_CLICK = 'integration:test:send:click',
  INTEGRATION_TEST_SEND_CONFLICT = 'integration:test:send:conflict',
  INTEGRATION_TEST_SEND_OVERRIDE = 'integration:test:send:override',
  INTEGRATION_TEST_SEND_NOT_FOUND = 'integration:test:send:not-found',
  INTEGRATION_TEST_SEND_FAIL = 'integration:test:send:fail',
  INTEGRATION_TEST_SEND_SUCCESS = 'integration:test:send:success'
}

export interface InitParams {
  instanceId: ProductSlugs | 'axe-account';
  gtmId?: string;
  amplitudeApiKey?: string;
  amplitudeTimeout?: number;
  config?: Config;
}

export interface IdentifyParams {
  user: AuthUser | SignedUpUser;
  billingStatus?: AxePurchaseState;
}

export interface SendAmplitudeEventParams<T> {
  event: T;
  data?: unknown;
  callback?: amplitude.Callback;
}

const isAuthUser = (user: AuthUser | SignedUpUser): user is AuthUser =>
  'roles' in user;

export default abstract class Analytics<T extends string> {
  protected amplitude: amplitudeJs.AmplitudeClient;
  protected AMPLITUDE_KEY_GROUP = 'organization';
  protected AMPLITUDE_KEY_ROLE = 'role';
  protected AMPLITUDE_KEY_STATUS = 'userStatus';
  protected AMPLITUDE_KEY_ENV = 'environment';
  // Active enterprise mirror. Subclasses that auto-merge enterprise context
  // onto their event payloads (e.g. `AxeDevtoolsWatcherAnalytics`) read this
  // directly from their `defaultData` getter; the storage and the Amplitude
  // user-property push are both managed by `setEnterprise` on this base class.
  protected enterprise: v2.Enterprise | null = null;
  public distinctIdCookie: Cookie | undefined;
  public didInitializeAmplitude = false;
  protected amplitudeTimeout: number;
  protected persistentEventProperties: Record<string, unknown> = {};

  constructor({
    instanceId,
    gtmId,
    amplitudeApiKey,
    amplitudeTimeout,
    config
  }: InitParams) {
    this.amplitude = amplitudeJs.getInstance(instanceId);
    this.amplitudeTimeout = amplitudeTimeout || DEFAULT_TIMEOUT;

    if (amplitudeApiKey) {
      const opts: Config = {
        includeUtm: true,
        saveParamsReferrerOncePerSession: false,
        includeGclid: true,
        includeReferrer: true,
        secureCookie: true,
        sameSiteCookie: 'Lax',
        ...config
      };

      this.amplitude.init(amplitudeApiKey, undefined, opts);
      this.didInitializeAmplitude = true;
    } else {
      // Reset (for tests)
      this.didInitializeAmplitude = false;
    }

    // Only initialize GTM when provided an ID.
    if (gtmId) {
      TagManager.initialize({ gtmId });
    }

    // Lazily fetch/set the distinct ID cookie.
    getDistinctIdCookie({ host: window.location.origin }).then(
      cookie => (this.distinctIdCookie = cookie)
    );
  }

  public identify({
    user,
    billingStatus = 'none'
  }: IdentifyParams): Promise<unknown> {
    if (!this.didInitializeAmplitude) {
      return Promise.resolve();
    }

    this.amplitude.setUserId(user.id);

    if (user.company) {
      this.amplitude.setGroup(this.AMPLITUDE_KEY_GROUP, user.company);
    }

    const id = new this.amplitude.Identify();
    if (isAuthUser(user)) {
      const role = user.roles.find(r =>
        // Cast to avoid "Argument of type 'string' is not assignable to parameter of type" error.
        (USER_JOB_ROLES as unknown as string[]).includes(r)
      );
      if (role) {
        id.set(this.AMPLITUDE_KEY_ROLE, role);
      }
    }
    id.set(this.AMPLITUDE_KEY_STATUS, billingStatus);
    id.set(this.AMPLITUDE_KEY_ENV, window.appConfig.DEPLOY_ENV || '');

    return new Promise(resolve => this.amplitude.identify(id, resolve));
  }

  /**
   * Mirrors the active enterprise onto the analytics instance and pushes the
   * foundational enterprise-context bundle onto the Amplitude user profile.
   * Available to every product that extends `Analytics`, but **today only
   * `AxeDevtoolsWatcherAnalytics` (DevHub) calls it** — Pro, Mobile, Auditor,
   * and Account adoption is tracked as follow-up work. Until those land,
   * charts filtering on `subscriptions` / `enterpriseName` / `isOrgAdmin`
   * reflect DevHub events only; cross-product analyses will be incomplete.
   *
   * On a truthy enterprise, sets:
   * - `enterpriseId` (string) — from `enterprise.id`
   * - `enterpriseName` (string) — from `enterprise.name`
   * - `isOrgAdmin` (boolean) — caller-supplied; canonical source is
   *   `useEnterprises().isAdmin`. Pinned here so future product adoptions
   *   pass the same signal and the cohort dimension stays coherent. Ignored
   *   on `null` — the unset path removes the key entirely, so a stale admin
   *   signal cannot survive logout regardless of the value passed.
   * - `subscriptions` (string[]) — deduped array of canonical `product_slug`
   *   values for subscriptions in an active state (`paid` or `trialing`).
   *   The actual values come from `ProductSlugs` (re-exported from
   *   `@deque/billing-utils`); the strings the array can contain include
   *   (non-exhaustive): `'axe-devtools-watcher'`, `'axe-devtools-mobile'`,
   *   `'axe-devtools-extension'`, `'axe-devtools-mobile-analyzer'`,
   *   `'axe-linter'`, `'axe-auditor'`, `'axe-monitor'`, `'axe-reports'`,
   *   `'axe-spider'`, `'deque-university'`. Use these strings literally in
   *   chart filters (`subscriptions contains 'axe-devtools-watcher'`).
   *
   * On `null`, unsets the four keys above on the Amplitude profile so a
   * stale value from a prior session does not leak onto subsequent events
   * within the same authenticated session. Note: this protection only
   * applies to the logout → login flow within one tab. A hard browser close
   * leaves the previous user's profile values intact until the next
   * `setEnterprise` call.
   *
   * Subclasses do **not** need to override this method to keep a local copy
   * of the enterprise — the `protected enterprise` field on this class is
   * the single source of truth and is available to subclass `defaultData`
   * getters that auto-merge enterprise context onto event payloads.
   */
  public setEnterprise(
    enterprise: v2.Enterprise | null,
    isOrgAdmin: boolean
  ): void {
    this.enterprise = enterprise;
    if (!this.didInitializeAmplitude) {
      return;
    }
    if (enterprise) {
      this.amplitude.setUserProperties({
        enterpriseId: enterprise.id,
        enterpriseName: enterprise.name,
        isOrgAdmin,
        subscriptions: getActiveSubscriptions(enterprise.subscriptions)
      });
    } else {
      const id = new this.amplitude.Identify();
      ENTERPRISE_USER_PROPERTY_KEYS.forEach(key => id.unset(key));
      this.amplitude.identify(id);
    }
  }

  protected sendAmplitudeEvent({
    event,
    data
  }: SendAmplitudeEventParams<T>): void | Promise<void> {
    if (!this.didInitializeAmplitude) {
      return;
    }
    const timeoutPromise = new Promise<void>(resolve =>
      setTimeout(() => {
        resolve();
      }, this.amplitudeTimeout)
    );

    const eventData = {
      ...this.persistentEventProperties,
      ...(typeof data === 'object' && data !== null ? data : {})
    };

    const amplitudePromise = new Promise<void>((resolve, reject) => {
      this.amplitude.logEvent(event, eventData, (status, response, details) => {
        if (status === 200) {
          return resolve();
        }

        reject(
          new Error(
            `An error occurred while tracking the event: ${response} due to ${details?.reason}`
          )
        );
      });
    });

    return Promise.race([amplitudePromise, timeoutPromise]);
  }

  public setEnterpriseId(enterpriseId: string | null): void {
    this.setPersistentEventProperty('enterpriseId', enterpriseId);

    if (!this.didInitializeAmplitude) {
      return;
    }

    if (enterpriseId) {
      this.amplitude.setUserProperties({ enterpriseId });
    } else {
      const id = new this.amplitude.Identify();
      id.unset('enterpriseId');
      this.amplitude.identify(id);
    }
  }

  public setPersistentEventProperty(key: string, value: unknown): void {
    if (value === null || value === undefined) {
      const actualProperties = Object.entries(
        this.persistentEventProperties
      ).reduce<Record<string, unknown>>((acc, [k, v]) => {
        if (k !== key) {
          acc[k] = v;
        }

        return acc;
      }, {});

      this.persistentEventProperties = actualProperties;
      return;
    }

    this.persistentEventProperties = {
      ...this.persistentEventProperties,
      [key]: value
    };
  }

  public signUp() {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.SIGNUP as T
    });
  }

  public async viewSignUp(params: { href: string }) {
    const data = await this.processDistinctIdCookieData(
      params.href,
      'webapp_organic_signup'
    );
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.VIEW_SIGNUP as T,
      data
    });
  }

  public clickGitHub() {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.CLICK_GITHUB as T
    });
  }

  public clickGoogle() {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.CLICK_GOOGLE as T
    });
  }

  public clickCreateAccount() {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.CLICK_CREATE_ACCOUNT as T
    });
  }

  public clickAxeDevtools() {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.CLICK_AXE_DEVTOOLS as T
    });
  }

  public trialStart(isTrialReactivation: boolean) {
    const cookieData = this.distinctIdCookie?.data;

    this.amplitude.setUserProperties({
      original_landing_page: cookieData?.original_landing_page,
      original_utm_source: cookieData?.original_utm_source
    });

    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.TRIAL_START as T,
      data: {
        // When possible, attach the landing page and utm_source the user came from.
        original_landing_page: cookieData?.original_landing_page,
        original_utm_source: cookieData?.original_utm_source,
        reactivation: isTrialReactivation
      }
    });
  }

  public acceptTerms() {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.ACCEPT_TERMS as T
    });
  }

  public clickAddUsers() {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.CLICK_ADD_USERS as T
    });
  }

  public clickChangePlan() {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.CLICK_CHANGE_PLAN as T
    });
  }

  public submitLicenseChange(data: {
    licenseCount: number;
    prevLicenseCount: number;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.SUBMIT_LICENSE_CHANGE as T,
      data
    });
  }

  public integrationConnect(data: {
    integration: SupportedIntegrationProductSlugs;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_CONNECT as T,
      data
    });
  }

  public integrationConnectFail(data: {
    integration: SupportedIntegrationProductSlugs;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_CONNECT_FAIL as T,
      data
    });
  }

  public integrationDisconnect(data: {
    integration: SupportedIntegrationProductSlugs;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_DISCONNECT as T,
      data
    });
  }

  public integrationDisconnectFail(data: {
    integration: SupportedIntegrationProductSlugs;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_DISCONNECT_FAIL as T,
      data
    });
  }

  public integrationTemplateAddClick(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    connectionId: string;
    enterpriseId: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEMPLATE_ADD_CLICK as T,
      data
    });
  }

  public integrationTemplateAddCancel(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    connectionId: string;
    enterpriseId: string;
    project?: string;
    issueType?: string;
    template?: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEMPLATE_ADD_CANCEL as T,
      data
    });
  }

  public integrationTemplateAddSave(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    connectionId: string;
    enterpriseId: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEMPLATE_ADD_SAVE as T,
      data
    });
  }

  public integrationTemplateAddFail(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    connectionId: string;
    enterpriseId: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEMPLATE_ADD_FAIL as T,
      data
    });
  }

  public integrationTemplateEditClick(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    connectionId: string;
    enterpriseId: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEMPLATE_EDIT_CLICK as T,
      data
    });
  }

  public integrationTemplateEditCancel(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    connectionId: string;
    enterpriseId: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEMPLATE_EDIT_CANCEL as T,
      data
    });
  }

  public integrationTemplateEditSave(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    connectionId: string;
    enterpriseId: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEMPLATE_EDIT_SAVE as T,
      data
    });
  }

  public integrationTemplateEditFail(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    connectionId: string;
    enterpriseId: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEMPLATE_EDIT_FAIL as T,
      data
    });
  }

  public integrationTemplateDeleteClick(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    connectionId: string;
    enterpriseId: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEMPLATE_DELETE_CLICK as T,
      data
    });
  }

  public integrationTemplateDeleteFail(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    connectionId: string;
    enterpriseId: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEMPLATE_DELETE_FAIL as T,
      data
    });
  }

  public integrationTemplateDeleteSuccess(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    connectionId: string;
    enterpriseId: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEMPLATE_DELETE_SUCCESS as T,
      data
    });
  }

  public integrationIssueSendClick(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    multiple: boolean;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_ISSUE_SEND_CLICK as T,
      data
    });
  }

  public integrationIssueSendSuccess(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    multiple: boolean;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_ISSUE_SEND_SUCCESS as T,
      data
    });
  }

  public integrationIssueSendFail(data: {
    integration:
      | SupportedIntegrationProductSlugs
      | ProductSlugs.axeAuditorIntegration;
    project: string;
    issueType: string;
    template: string;
    multiple: boolean;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_ISSUE_SEND_FAILURE as T,
      data
    });
  }

  public userManagementExportSuccess() {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.USER_MANAGEMENT_EXPORT_SUCCESS as T
    });
  }

  protected async processDistinctIdCookieData(
    href: string,
    defaultOriginalUtmSource: string
  ): Promise<{ original_landing_page: string; original_utm_source: string }> {
    const cookieData = this.distinctIdCookie?.data;

    if (
      !cookieData?.original_landing_page &&
      !cookieData?.original_utm_source
    ) {
      const data = {
        original_landing_page: href,
        original_utm_source: defaultOriginalUtmSource
      };

      const host = removeTrailingSlash(window.appConfig.API_SERVER_URL || '');
      await setDistinctIdCookie({ data }, { host });
      this.amplitude.setUserProperties(data);
      return data;
    }

    return {
      original_landing_page: cookieData.original_landing_page as string,
      original_utm_source: cookieData.original_utm_source as string
    };
  }

  public integrationTestSendClick(data: {
    integration: string;
    source: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEST_SEND_CLICK as T,
      data
    });
  }

  public integrationTestSendConflict(data: {
    integration: string;
    source: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEST_SEND_CONFLICT as T,
      data
    });
  }

  public integrationTestSendOverride(data: {
    integration: string;
    source: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEST_SEND_OVERRIDE as T,
      data
    });
  }

  public integrationTestSendNotFound(data: {
    integration: string;
    source: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEST_SEND_NOT_FOUND as T,
      data
    });
  }

  public integrationTestSendFail(data: {
    integration: string;
    source: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEST_SEND_FAIL as T,
      data
    });
  }

  public integrationTestSendSuccess(data: {
    integration: string;
    source: string;
  }) {
    return this.sendAmplitudeEvent({
      event: AnalyticsAmplitudeEvents.INTEGRATION_TEST_SEND_SUCCESS as T,
      data
    });
  }
}
